import { defineStore } from 'pinia'
import { ref } from 'vue'
import { 
  getVipPackages,
  createVipPackage,
  purchaseVipPackage,
  getCustomerVipPackages,
  updateVipPackage,
  toggleVipPackageActive,
  getVipIncomeStatistics,
  getVipAuditLogs,
  getVipPackageTypes,
  createVipPackageType,
  updateVipPackageType,
  deleteVipPackageType,
  updateCustomerVip,
  adjustCustomerVipHours
} from '@/api/vip'

export const useVipStore = defineStore('vip', () => {
  // State
  const vipPackages = ref([])
  const customerVipPackages = ref([])
  const vipStatistics = ref(null)
  const auditLogs = ref([])
  const packageTypes = ref([])
  const loading = ref(false)
  const packageTypesLoading = ref(false)
  
  // Actions
  const fetchVipPackages = async (activeOnly = false) => {
    loading.value = true
    try {
      const res = await getVipPackages({ activeOnly })
      
      if (res.success) {
        vipPackages.value = res.data || []
        return { success: true, data: vipPackages.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Fetch error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const createPackage = async (packageData) => {
    try {
      const res = await createVipPackage(packageData)
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Create error:', error)
      return { success: false, error: error.message }
    }
  }

  const purchasePackage = async (packageId, beneficiaryCustomerId, buyerCustomerId) => {
    try {
      const res = await purchaseVipPackage({
        packageId,
        beneficiaryCustomerId,
        buyerCustomerId
      })
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Purchase error:', error)
      return { success: false, error: error.message }
    }
  }

  const fetchCustomerVipPackages = async (customerId, activeOnly = false) => {
    loading.value = true
    try {
      const res = await getCustomerVipPackages(customerId, { activeOnly })
      
      if (res.success) {
        customerVipPackages.value = res.data || []
        return { success: true, data: customerVipPackages.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Customer VIP error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const updatePackage = async (id, packageData) => {
    try {
      const res = await updateVipPackage(id, packageData)
      
      if (res.success) {
        // Update local list
        const index = vipPackages.value.findIndex(p => p.id === parseInt(id))
        if (index !== -1) {
          vipPackages.value[index] = { 
            ...vipPackages.value[index], 
            ...packageData,
            id: parseInt(id)
          }
        }
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Update error:', error)
      return { success: false, error: error.message }
    }
  }

  const deletePackage = async (id) => {
    try {
      // Use toggle-active for soft delete/restore
      const res = await toggleVipPackageActive(id)
      
      if (res.success) {
        // Toggle active status in local list
        const pkg = vipPackages.value.find(p => p.id === id)
        if (pkg) {
          pkg.isActive = !pkg.isActive
        }
        return { success: true, message: res.message }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Toggle active error:', error)
      return { success: false, error: error.message }
    }
  }

  const fetchVipStatistics = async (params) => {
    loading.value = true
    try {
      const res = await getVipIncomeStatistics(params)
      
      if (res.success) {
        vipStatistics.value = res.data || null
        return { success: true, data: vipStatistics.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Statistics error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchAuditLogs = async (params) => {
    loading.value = true
    try {
      const res = await getVipAuditLogs(params)
      
      if (res.success) {
        // Handle different API response structures
        const data = res.data || {}
        const items = data.items || data.list || data.records || []
        const total = data.total || data.totalCount || data.count || 0
        
        auditLogs.value = items
        
        console.log('[VIP Store] Audit logs response:', {
          items: items.length,
          total,
          page: data.page || params.page || 1,
          pageSize: data.pageSize || params.pageSize || 20
        })
        
        return { 
          success: true, 
          data: items,
          total: total,
          page: data.page || params.page || 1,
          pageSize: data.pageSize || params.pageSize || 20
        }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Audit logs error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  // Package Type Actions
  const fetchPackageTypes = async (isActive) => {
    packageTypesLoading.value = true
    try {
      const params = isActive !== null && isActive !== undefined ? { isActive } : {}
      const res = await getVipPackageTypes(params)
      
      console.log('[VIP Store] Package types raw response:', JSON.stringify(res, null, 2))
      
      if (res && res.success) {
        // Handle different API response structures
        let data = res.data
        
        console.log('[VIP Store] Initial data type:', typeof data, Array.isArray(data) ? 'array' : 'object')
        
        // If data is an object, extract the array
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          console.log('[VIP Store] Data is object, keys:', Object.keys(data))
          
          // Directly access packageTypes
          if (data.packageTypes && Array.isArray(data.packageTypes)) {
            data = data.packageTypes
            console.log('[VIP Store] Extracted packageTypes array:', data.length, 'items')
          } else if (data.items && Array.isArray(data.items)) {
            data = data.items
          } else if (data.list && Array.isArray(data.list)) {
            data = data.list
          } else if (data.records && Array.isArray(data.records)) {
            data = data.records
          } else if (data.data && Array.isArray(data.data)) {
            data = data.data
          } else {
            console.warn('[VIP Store] Could not find array in data object')
            data = []
          }
        }
        
        // Ensure we always have an array
        const dataArray = Array.isArray(data) ? data : []
        // Ensure boolean conversion for isActive field
        packageTypes.value = dataArray.map(item => ({
          ...item,
          isActive: Boolean(item.isActive)
        }))
        console.log('[VIP Store] Final package types:', packageTypes.value.length, 'items')
        if (packageTypes.value.length > 0) {
          console.log('[VIP Store] Sample item:', packageTypes.value[0])
        }
        return { success: true, data: packageTypes.value }
      } else {
        console.warn('[VIP Store] Package types request failed:', res?.message)
        packageTypes.value = []
        return { success: false, message: res?.message || 'Failed to load package types' }
      }
    } catch (error) {
      console.error('[VIP Store] Fetch package types error:', error)
      packageTypes.value = []
      return { success: false, error: error.message }
    } finally {
      packageTypesLoading.value = false
    }
  }

  const createPackageType = async (packageTypeData) => {
    try {
      const res = await createVipPackageType(packageTypeData)
      
      if (res.success) {
        // Refresh package types list
        await fetchPackageTypes()
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Create package type error:', error)
      return { success: false, error: error.message }
    }
  }

  const updatePackageType = async (id, packageTypeData) => {
    try {
      const res = await updateVipPackageType(id, packageTypeData)
      
      if (res.success) {
        // Update local list
        const index = packageTypes.value.findIndex(p => p.id === parseInt(id))
        if (index !== -1) {
          packageTypes.value[index] = { 
            ...packageTypes.value[index], 
            ...packageTypeData,
            id: parseInt(id)
          }
        }
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Update package type error:', error)
      return { success: false, error: error.message }
    }
  }

  const deletePackageType = async (id) => {
    try {
      const res = await deleteVipPackageType(id)
      
      if (res.success) {
        // Remove from local list
        const index = packageTypes.value.findIndex(p => p.id === id)
        if (index !== -1) {
          packageTypes.value.splice(index, 1)
        }
        return { success: true, message: res.message }
      }
      
      return { success: false, message: res.message || 'Delete failed' }
    } catch (error) {
      console.error('[VIP Store] Delete package type error:', error)
      // Extract exact error message from API response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.Message || 
                          error.message || 
                          'Delete failed'
      return { success: false, error: errorMessage, message: errorMessage }
    }
  }

  const updateCustomerVipPackage = async (id, data) => {
    try {
      const res = await updateCustomerVip(id, data)
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Update customer VIP error:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.Message || 
                          error.message || 
                          'Update failed'
      return { success: false, error: errorMessage, message: errorMessage }
    }
  }

  const adjustCustomerVipHoursAction = async (id, data) => {
    try {
      const res = await adjustCustomerVipHours(id, data)
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[VIP Store] Adjust customer VIP hours error:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.Message || 
                          error.message || 
                          'Adjustment failed'
      return { success: false, error: errorMessage, message: errorMessage }
    }
  }

  return {
    // State
    vipPackages,
    customerVipPackages,
    vipStatistics,
    auditLogs,
    packageTypes,
    loading,
    packageTypesLoading,
    
    // Actions
    fetchVipPackages,
    createPackage,
    purchasePackage,
    fetchCustomerVipPackages,
    updatePackage,
    deletePackage,
    fetchVipStatistics,
    fetchAuditLogs,
    fetchPackageTypes,
    createPackageType,
    updatePackageType,
    deletePackageType,
    updateCustomerVipPackage,
    adjustCustomerVipHours: adjustCustomerVipHoursAction
  }
})
